/** Must stay aligned with AssistantScreenContext.MaximumDetails. */
export const MAXIMUM_ASSISTANT_SCREEN_DETAILS = 8;

/** Must stay aligned with AssistantScreenContext.MaximumValueCharacters. */
export const MAXIMUM_ASSISTANT_SCREEN_VALUE_CHARACTERS = 256;

/** The one record a page is about, as the page names it. */
export interface AssistantScreenSubject {
  /** What kind of record - "proposal", "work agreement". */
  type: string;
  /** A hint for a lookup. The server authorizes it as it would an id the user typed. */
  id?: string;
  /** The name the page shows, so the answer can use it rather than the id. */
  name?: string;
}

export interface AssistantScreenDetail {
  label: string;
  value: string;
}

/**
 * What a page says the user is looking at, beyond its address.
 *
 * A page declares this on purpose, through `useAssistantScreenContext`, and the assistant sends
 * it with each turn. Nothing reads the DOM. The server renders it as quoted data and trusts none
 * of it - see `AssistantScreenContext` in `api/AIAssistant`.
 */
export interface AssistantScreenContext {
  /** What kind of page this is, in words - "Active proposal". */
  page: string;
  subject?: AssistantScreenSubject;
  /** The tab, step or section in view. */
  section?: string;
  /** The drawer, panel or dialog open over the page. */
  openPanel?: string;
  details?: AssistantScreenDetail[];
}

function bounded(value: string): string;
function bounded(value: string | undefined): string | undefined;
function bounded(value: string | undefined): string | undefined {
  return value === undefined ? undefined : value.slice(0, MAXIMUM_ASSISTANT_SCREEN_VALUE_CHARACTERS);
}

/**
 * `context` cut to what the request contract admits.
 *
 * The server refuses an oversized screen context with a 400, and a 400 costs the user their
 * question. A page naming a record by a long title is not a reason to lose the turn, so the
 * values are cut here, where the contract's bounds are known, rather than at every page.
 * Blank optional values are dropped rather than sent as empty strings.
 */
export function boundAssistantScreenContext(context: AssistantScreenContext): AssistantScreenContext {
  const present = (value: string | undefined) => (value?.trim() ? bounded(value) : undefined);

  return {
    page: bounded(context.page),
    subject: context.subject && {
      type: bounded(context.subject.type),
      id: present(context.subject.id),
      name: present(context.subject.name),
    },
    section: present(context.section),
    openPanel: present(context.openPanel),
    details: context.details
      ?.filter((detail) => detail.label.trim())
      .slice(0, MAXIMUM_ASSISTANT_SCREEN_DETAILS)
      .map((detail) => ({ label: bounded(detail.label), value: bounded(detail.value) })),
  };
}
